import { Component } from '@theme/component';

/** When the badge was last closed. */
const DISMISSED_AT_STORAGE_KEY = 'mint-floating-badge-dismissed-at';
/** How long the badge stays closed, across page loads, after someone closes it. */
const DISMISS_DURATION = 3 * 60 * 1000; // 3 minutes

/**
 * Image badge pinned to the bottom-left corner of every page, with a close button.
 *
 * It's rendered `hidden` (except in the theme editor) so it can't flash up on a page
 * load right after being closed. This reveals it once its image has loaded.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image
 *
 * @extends {Component<Refs>}
 */
class FloatingBadgeComponent extends Component {
  requiredRefs = ['image'];

  connectedCallback() {
    super.connectedCallback();
    if (!this.hidden || this.#recentlyDismissed()) return;

    // Wait for the image, so the close button never shows up on its own.
    const { image } = this.refs;
    if (image.complete) this.#reveal();
    else image.addEventListener('load', this.#reveal, { once: true });
  }

  dismiss() {
    this.hidden = true;
    try {
      localStorage.setItem(DISMISSED_AT_STORAGE_KEY, String(Date.now()));
    } catch {} // Storage blocked (private mode) — it just won't stay closed.
  }

  #reveal = () => {
    this.hidden = false;
  };

  #recentlyDismissed() {
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_STORAGE_KEY));
      return Date.now() - dismissedAt < DISMISS_DURATION;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('floating-badge-component')) {
  customElements.define('floating-badge-component', FloatingBadgeComponent);
}
